import { HttpClient, HttpEvent, HttpResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Image, KeyValueDTO, PrincipalService } from '@ms/core';
import { Pageable } from '@ms/core/components/table/pageable.model';
import { EventManager } from '@ms/core/service/event-manager.service';
import { Observable, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HTTP_CLIENT_XHR } from 'app/system/http-client-provider';
import {
    IPreRegisterStudent,
    IPreRegisterStudentPageableDTO,
    PreRegisterStudent,
    PreRegisterStudentApprove,
    PreRegisterStudentComplete,
    PreRegisterStudentSearch,
} from '../entrance-exam';
import { PreRegisterStudentEmail } from '../entrance-exam/model/pre-register-student-email.model';
import { PreRegisterStudentPartialUpdate } from '../entrance-exam/model/pre-register-student-partial-update.model';
import { PreRegisterStudentSms } from '../entrance-exam/model/pre-register-student-sms.model';
import { httpParams, logRequest } from '../shared/http-utils';
import {
    IPreRegisterStudentRepresentative,
    PreRegisterStudentRepresentative,
} from '../shared/model/student-representative.model';
import { IPage } from '../shared/page/page.model';
import { convertRequest } from '../shared/utils';
import { Student } from '../students/model/student.model';
import {
    PreRegStudChartSearch,
    PreRegStudStatusTotal,
} from './../../layout/components/content/charts/pre-reg-student-chart/pre-reg-student-chart.model';
import { PreRegisteredStudentAutocompleteSearchDTO as PreRegStudAutocompleteSearchDTO } from './../shared/model/pre-registered-student-search.model';
import { SchoolService } from './school.service';

@Injectable({
    providedIn: 'root',
})
export class EntranceExamService implements OnDestroy {
    baseUrl = '/school-service/api/entrance-exam';
    subscriber: Subscription;
    xhrHttp = inject(HTTP_CLIENT_XHR);
    constructor(
        private http: HttpClient,
        private schoolService: SchoolService,
        private principalService: PrincipalService,
        private eventManager: EventManager
    ) {
        this.subscriber = this.eventManager.subscribe('entrance-exam-mail', content => {
            console.log('Message received', content);
        });
    }

    getStudentById(id: string): Observable<HttpResponse<IPreRegisterStudent>> {
        return this.http.get<IPreRegisterStudent>(`${this.baseUrl}/${id}`, {
            observe: 'response',
        });
    }

    register(studentId: string): Observable<HttpResponse<PreRegisterStudent>> {
        return this.http.get<PreRegisterStudent>(`${this.baseUrl}/${studentId}/register`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    unRegister(studentId: string): Observable<HttpResponse<PreRegisterStudent>> {
        return this.http.get<PreRegisterStudent>(`${this.baseUrl}/${studentId}/un-register`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getImage(id: string): Observable<Image> {
        return this.http.get<Image>(`${this.baseUrl}/file/${id}`);
    }

    search(preRegisterStudentSearchDTO: PreRegisterStudentSearch): Observable<HttpResponse<IPreRegisterStudent[]>> {
        preRegisterStudentSearchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IPreRegisterStudent[]>(`${this.baseUrl}/search`, preRegisterStudentSearchDTO, {
            observe: 'response',
        });
    }

    searchWithPagination(
        searchDTO: PreRegisterStudentSearch,
        params?: Pageable
    ): Observable<HttpResponse<IPage<IPreRegisterStudentPageableDTO>>> {
        searchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<IPage<IPreRegisterStudentPageableDTO>>(`${this.baseUrl}/search/pageable`, searchDTO, {
            observe: 'response',
            params: httpParams(params),
        });
    }

    getStudentsByStatus(statuses: string[], student: string): Observable<HttpResponse<KeyValueDTO[]>> {
        const searchDTO = new PreRegStudAutocompleteSearchDTO(
            statuses,
            student,
            this.schoolService.getActiveSchool().id
        );
        return this.http.post<KeyValueDTO[]>(`${this.baseUrl}/autocomplete`, searchDTO, { observe: 'response' }).pipe(
            map(results => results),
            catchError(() => [])
        );
    }

    exportXLS(searchDTO: PreRegisterStudentSearch): Observable<HttpResponse<any>> {
        searchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post(`${this.baseUrl}/export/xls`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    map(response: IPreRegisterStudent): IPreRegisterStudent {
        const result: IPreRegisterStudent = Object.assign({}, response);
        result.profile = Object.assign({}, result.profile);
        result.profile.address = Object.assign({}, result.profile.address);

        result.profile.citizenship = Object.assign({ id: '' }, result.profile.citizenship);
        result.profile.nationality = Object.assign({ id: '' }, result.profile.nationality);
        result.profile.address.country = Object.assign({ id: '' }, result.profile.address.country);
        result.profile.documentInfo = Object.assign({}, result.profile.documentInfo);
        result.profile.contactInfo = Object.assign({}, result.profile.contactInfo);
        return result;
    }

    doConvertStudent(id: string): Observable<HttpResponse<Student>> {
        return this.http.get<Student>(`${this.baseUrl}/convert/${id}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doCompleteStudentsPreRegistration(
        data: PreRegisterStudentComplete
    ): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/complete`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doBulkDeleteStudentsPreRegistration(list: string[]): Observable<HttpResponse<any>> {
        return this.http.put<any>(`${this.baseUrl}/bulk-delete`, list, { observe: 'response', headers: logRequest() });
    }

    doCancelCompletePreRegistration(data: PreRegisterStudentComplete): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/uncomplete`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doApprovePreRegistration(data: PreRegisterStudentApprove): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/approve`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doUnApprovePreRegistration(data: PreRegisterStudentApprove): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/unapprove`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doPassPreRegistration(data: PreRegisterStudentApprove): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/pass`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doFailPreRegistration(data: PreRegisterStudentApprove): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/fail`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    doReservePreRegistration(data: PreRegisterStudentApprove): Observable<HttpResponse<IPreRegisterStudent[]>> {
        data.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.put<IPreRegisterStudent[]>(`${this.baseUrl}/reserve`, data, {
            observe: 'response',
            headers: logRequest(),
        });
    }
    doSendEmail(dto: PreRegisterStudentEmail, formData: FormData): Observable<HttpEvent<any>> {
        if (!formData) {
            formData = new FormData();
        }
        if (dto) {
            dto.schoolId = this.schoolService.getActiveSchool().id;
            dto = convertRequest(dto);
            formData.set(
                'dto',
                new Blob([JSON.stringify(dto)], {
                    type: 'application/json',
                })
            );
        }
        return this.xhrHttp.post<FormData>(`${this.baseUrl}/send-email`, formData, {
            reportProgress: true,
            observe: 'events',
        });
    }

    doSendSms(dto: PreRegisterStudentSms) {
        dto.schoolId = this.schoolService.getActiveSchool().id;

        return this.http.post<PreRegisterStudentSms>(`${this.baseUrl}/send-sms`, dto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    uploadFile(entityId: string, attachmentId: string, formData: FormData) {
        const tenantId = this.principalService.authenticatedUser.tenantId;
        return this.xhrHttp.post<any>(`${this.baseUrl}/${tenantId}/${entityId}/attachment/${attachmentId}`, formData, {
            reportProgress: true,
            observe: 'events',
        });
    }

    downloadFile(entityId: string, id: string) {
        return this.http.get(`${this.baseUrl}/${entityId}/attachment/${id}`, {
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    deleteFile(entityId: string, id: string) {
        return this.http.delete(`${this.baseUrl}/${entityId}/attachment/${id}`, {
            headers: logRequest(),
        });
    }

    getAttachments(entityId: string, examId?: string) {
        return this.http.get(`${this.baseUrl}/${entityId}/attachment${examId ? `/${examId}/exam` : ''}`);
    }

    createStudent(preRegisterStudent: PreRegisterStudent): Observable<HttpResponse<IPreRegisterStudent>> {
        return this.http.post<IPreRegisterStudent>(this.baseUrl, preRegisterStudent, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateStudent(preRegisterStudent: PreRegisterStudent): Observable<HttpResponse<IPreRegisterStudent>> {
        return this.http.put<IPreRegisterStudent>(this.baseUrl, preRegisterStudent, {
            observe: 'response',
        });
    }

    deleteStudent(id: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${id}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getStatusTotals(search: PreRegStudChartSearch): Observable<HttpResponse<PreRegStudStatusTotal>> {
        return this.http.post<PreRegStudStatusTotal>(`${this.baseUrl}/chart-search`, search, {
            observe: 'response',
        });
    }

    updatePhone(dto: PreRegisterStudentPartialUpdate): Observable<HttpResponse<any>> {
        return this.http.put<PreRegisterStudentPartialUpdate>(`${this.baseUrl}/update-phone`, dto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateEmail(dto: PreRegisterStudentPartialUpdate): Observable<HttpResponse<any>> {
        return this.http.put<PreRegisterStudentPartialUpdate>(`${this.baseUrl}/update-email`, dto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    mapRepresentatives(studentRepresentative: IPreRegisterStudentRepresentative) {
        const result: PreRegisterStudentRepresentative = Object.assign({}, studentRepresentative);
        result.profile = Object.assign({}, result.profile);
        result.profile.address = Object.assign({}, result.profile.address);
        result.profile.address.state = Object.assign({ id: '' }, result.profile.address.state);
        result.profile.address.country = Object.assign({ id: '' }, result.profile.address.country);
        result.profile.documentInfo = Object.assign({}, result.profile.documentInfo);
        result.profile.contactInfo = Object.assign({}, result.profile.contactInfo);
        return result;
    }

    downloadExamPaper(id: string) {
        return this.http.get(`${this.baseUrl}/${id}/download-exam-paper`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    downloadRegistrationForm(id: string) {
        return this.http.get(`${this.baseUrl}/${id}/download-registration-form`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    ngOnDestroy() {
        this.subscriber.unsubscribe();
    }
}
